import type { TrialResult } from '@/domain/core/types';
import type { DotProbeMeta } from '@/domain/tasks/dotprobe';
import type { FrameQuality } from '@/engine/Clock';
import type { TrialSink } from '@/engine/TrialSink';
import { appendTrials, finishSession, logEvent } from './repositories/sessions';
import type { SessionArm, TrialRecord } from './types';

/**
 * 엔진 → IndexedDB. 블록 경계에서만 불린다 (Runner 의 계약).
 * 결과와 조건 메타를 한 레코드로 평탄화해 저장한다 — 차이점수는 저장하지 않는다.
 */
export class DexieSink implements TrialSink {
  #sessionId: string;
  #arm: SessionArm;
  #meta: readonly DotProbeMeta[];
  #flushed = 0;

  constructor(sessionId: string, arm: SessionArm, meta: readonly DotProbeMeta[]) {
    this.#sessionId = sessionId; this.#arm = arm; this.#meta = meta;
  }

  #toRecords(results: readonly TrialResult[]): TrialRecord[] {
    return results.slice(this.#flushed).map((r) => {
      const m = this.#meta[r.trialIndex];
      if (!m) throw new Error(`meta 누락: trial ${r.trialIndex}`);
      return {
        sessionId: this.#sessionId, trialIndex: r.trialIndex, blockId: r.blockId,
        arm: this.#arm, trialType: m.trialType, pairId: m.pairId, threatSide: m.threatSide,
        probeSide: m.probeSide, congruency: m.congruency, soaMs: m.soaMs,
        onsetTs: r.onsetTs, responseTs: r.responseTs, rt: r.rt, response: r.response, correct: r.correct,
        timedOut: r.timedOut, aborted: r.aborted, inputSource: r.inputSource, handlerDelayMs: r.handlerDelayMs,
        prematureInputs: r.prematureInputs, phases: r.phases.map((p) => ({ ...p })),
      };
    });
  }

  async onBlockEnd(_blockId: string, results: readonly TrialResult[], quality: FrameQuality): Promise<void> {
    const recs = this.#toRecords(results);
    await appendTrials(recs);
    this.#flushed += recs.length;
    await finishSession(this.#sessionId, { quality, trialCount: this.#flushed });
  }

  async onAbort(_blockId: string, results: readonly TrialResult[], reason: string): Promise<void> {
    // 중단 시점까지 도달한 트라이얼은 보존한다. 직전 블록까지는 이미 flush 되어 있다.
    const reached = results.filter((r) => r.onsetTs !== null || r.aborted || r.response !== null);
    const recs = this.#toRecords(reached);
    if (recs.length) await appendTrials(recs);
    this.#flushed += recs.length;
    await logEvent({ sessionId: this.#sessionId, ts: performance.now(), at: new Date().toISOString(), topic: 'abort', payload: { reason } });
    await finishSession(this.#sessionId, { endedAt: new Date().toISOString(), abortedReason: reason, trialCount: this.#flushed });
  }
}
